import Papa from "papaparse";
import * as XLSX from "xlsx";

export const SCHEMAS = {
  fact_bookings: ["deal_id", "account_id", "rep_id", "segment", "region", "stage", "amount", "is_won", "created_at", "closed_at", "sales_cycle_days"],
  fact_stage_history: ["deal_id", "from_stage", "to_stage", "entered_at", "days_in_prev_stage"],
  fact_arr_movement: ["movement_id", "account_id", "segment", "type", "amount", "effective_date"],
  fact_gl: ["entry_id", "period", "cost_center", "account", "account_type", "amount", "vendor", "memo"],
  fact_invoice_line: ["invoice_id", "account_id", "line_id", "product", "period", "amount", "quantity"],
  plan: ["period", "cost_center", "account_type", "plan_amount"],
  dim_rep: ["rep_id", "name", "segment", "region", "tenure_months", "quota"],
  dim_account: ["account_id", "name", "segment", "region", "arpa", "industry"],
};

export function classify(headerRow) {
  const keys = headerRow.map((h) => String(h).toLowerCase().trim());
  for (const [name, cols] of Object.entries(SCHEMAS)) {
    const required = cols.slice(0, 4);
    if (required.every((c) => keys.includes(c))) return name;
  }
  return null;
}

const NUMERIC = new Set(["amount", "quantity", "plan_amount", "quota", "arpa", "tenure_months", "sales_cycle_days", "days_in_prev_stage"]);

function coerceValue(key, v) {
  if (NUMERIC.has(key)) return v === null || v === "" ? null : Number(v);
  if (key === "is_won") return v === true || String(v).toLowerCase() === "true" || v === 1;
  return v;
}

// derived fields the drill paths rely on
function addDerivedFields(table, out) {
  if (table === "fact_arr_movement" && out.effective_date) out.month = String(out.effective_date).slice(0, 7);
  if (table === "fact_bookings") {
    if (out.created_at) out.month = String(out.created_at).slice(0, 7);
    out.status = out.stage === "Closed Won" ? "won" : out.stage === "Closed Lost" ? "lost" : "open";
    out.is_won = out.status === "won";
  }
  if (table === "fact_invoice_line" && out.period) out.month = String(out.period).slice(0, 7);
}

function coerce(table, row) {
  const out = {};
  for (const [k, v] of Object.entries(row)) {
    const key = String(k).toLowerCase().trim();
    out[key] = coerceValue(key, v);
  }
  addDerivedFields(table, out);
  return out;
}

function enrichBookings(rows, accounts, reps) {
  for (const b of rows) {
    const a = accounts.get(b.account_id);
    const r = reps.get(b.rep_id);
    if (a) b.account_name = a.name;
    if (r) b.rep_name = r.name;
  }
}

function enrichAccountFact(row, accounts) {
  const a = accounts.get(row.account_id);
  if (!a) return;
  row.account_name = a.name;
  if (row.segment == null || row.segment === "") row.segment = a.segment;
  row.region = row.region || a.region;
  row.cohort = row.cohort || a.cohort || "Unassigned";
}

function enrich(tables) {
  const accounts = new Map((tables.dim_account || []).map((a) => [a.account_id, a]));
  const reps = new Map((tables.dim_rep || []).map((r) => [r.rep_id, r]));
  enrichBookings(tables.fact_bookings || [], accounts, reps);
  for (const t of ["fact_arr_movement", "fact_invoice_line"]) {
    for (const row of tables[t] || []) enrichAccountFact(row, accounts);
  }
  return tables;
}

export async function ingestFile(file) {
  const name = file.name.toLowerCase();
  const tables = {};
  if (name.endsWith(".csv")) {
    const text = await file.text();
    const parsed = Papa.parse(text, { header: true, skipEmptyLines: true });
    if (!parsed.data.length) throw new Error("CSV is empty");
    const target = classify(Object.keys(parsed.data[0]));
    if (!target) throw new Error(`Could not match CSV headers to any fact table. Expected one of: ${Object.keys(SCHEMAS).join(", ")}`);
    tables[target] = parsed.data.map((r) => coerce(target, r));
  } else {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array" });
    for (const sheet of wb.SheetNames) {
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[sheet], { defval: null });
      if (!rows.length) continue;
      const target = SCHEMAS[sheet.toLowerCase().trim()] ? sheet.toLowerCase().trim() : classify(Object.keys(rows[0]));
      if (target) tables[target] = rows.map((r) => coerce(target, r));
    }
    if (!Object.keys(tables).length) throw new Error("No sheet matched a known fact schema. Download the template for the expected format.");
  }
  return enrich(tables);
}

export function downloadTemplate(sampleTables) {
  const wb = XLSX.utils.book_new();
  for (const [name, cols] of Object.entries(SCHEMAS)) {
    const rows = (sampleTables[name] || []).slice(0, 5).map((r) => {
      const o = {};
      for (const c of cols) o[c] = r[c] ?? "";
      return o;
    });
    const ws = XLSX.utils.json_to_sheet(rows.length ? rows : [Object.fromEntries(cols.map((c) => [c, ""]))]);
    XLSX.utils.book_append_sheet(wb, ws, name.slice(0, 31));
  }
  XLSX.writeFile(wb, "fpna_fact_template.xlsx");
}

export function exportRowsXlsx(rows, filename) {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "data");
  XLSX.writeFile(wb, filename);
}
